import React from 'react';
import classNames from "classnames";

import {CheckCircleIcon} from "../icons/CheckCircleIcon";
import {InfoIcon} from "../icons/InfoIcon";
import {WarningIcon} from "../icons/WarningIcon";
import {DangerIcon} from "../icons/DangerIcon";
import {AiIcon} from "../icons/AiIcon";
import {CloseIcon} from "../icons/CloseIcon";
import {DismissibleStorageStrategy, useDismissibleStorage} from "../../hooks/useDismissibleStorage";


import './SectionMessage.css';


export type SectionMessageVariant = 'default' | 'error' | 'success' | 'warning' | 'info' | 'ai';

export interface SectionMessageProps {
    message: string | React.ReactNode;
    title?: string;
    className?: string;
    maxWidth?: string;
    variant: SectionMessageVariant;
    onClose?: () => void;
}

export const SectionMessage = React.forwardRef<HTMLDivElement, SectionMessageProps>((
    {
        message,
        title,
        className,
        variant,
        maxWidth,
        onClose,
    },
    ref
) => {
    const Icon = getVariantIcon(variant);
    const style = maxWidth ? {maxWidth} : {};
    return (
        <div
            className={classNames({
                'SectionMessage': true,
                [className!]: !!className,
                'SectionMessage--default': variant === 'default',
                'SectionMessage--success': variant === 'success',
                'SectionMessage--info': variant === 'info',
                'SectionMessage--warning': variant === 'warning',
                'SectionMessage--error': variant === 'error',
                'SectionMessage--ai': variant === 'ai',
            })}
            ref={ref}
            style={style}
        >
            {Icon ? <Icon size={20} className={'SectionMessage__icon'}/> : null}
            <div className={'SectionMessage__content'}>
                {title ? <div className={'SectionMessage__header body-large-bold'}>{title}</div> : null}
                <div className={'SectionMessage__message body-large'}>{message}</div>
            </div>
            {onClose ? <CloseIcon className="SectionMessage__closeIcon" size={20} onClick={onClose}/> : null}
        </div>
    )
});

export const getVariantIcon = (variant: SectionMessageVariant) => {
    switch (variant) {
        case "ai":
            return AiIcon;
        case "info":
            return InfoIcon;
        case "success":
            return CheckCircleIcon;
        case "warning":
            return WarningIcon;
        case "error":
            return DangerIcon;
        case "default":
        default:
            return null;
    }
}

interface SectionMessageWithStorageProps extends Omit<SectionMessageProps, "onClose"> {
    storageKey: string;
    dismissStrategy?: DismissibleStorageStrategy;
}

export const SectionMessageWithStorage = (
    ({storageKey, dismissStrategy, ...rest}: SectionMessageWithStorageProps) => {
        const {isHidden, hide} = useDismissibleStorage({storageKey, strategy: dismissStrategy});

        if (isHidden) {
            return null;
        }

        return (
            <SectionMessage
                {...rest}
                onClose={hide}
            />
        );
    }
);
